import { useState, useRef, useEffect, Fragment } from "react";
import { Menu, Transition } from "@headlessui/react";
import { X, Loader2, History, UserCircle, Clock, Sparkles } from "lucide-react";
import request from "../../utils/request";
import GenerationHistorySidebar from "./GenerationHistorySidebar";
import ConfirmModal from "../../components/ConfirmModal";


const LANGUAGES = [
  { value: "en", label: "English (US)" },
  { value: "en-uk", label: "English (UK)" },
  { value: "es", label: "Spanish" },
  { value: "fr", label: "French" },
  { value: "de", label: "German" },
  { value: "it", label: "Italian" },
  { value: "pt", label: "Portuguese" },
  { value: "pl", label: "Polish" },
  { value: "nl", label: "Dutch" },
  { value: "sl", label: "Slovenian" },
  { value: "el", label: "Greek" },
  { value: "cs", label: "Czech" },
  { value: "sv", label: "Swedish" },
  { value: "da", label: "Danish" },
  { value: "no", label: "Norwegian" },
  { value: "fi", label: "Finnish" },
  { value: "hu", label: "Hungarian" },
  { value: "ro", label: "Romanian" },
  { value: "tr", label: "Turkish" },
];

export default function CreateTab({ setActiveTab, registerGuard }) {
  // ─── Form state ──────────────────────────────────────────────────────────
  const [characterName, setCharacterName] = useState("");
  const [selectedGender, setSelectedGender] = useState("female");
  const [selectedLanguage, setSelectedLanguage] = useState("en");
  const [appearance, setAppearance] = useState("");
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [characterImage, setCharacterImage] = useState(null);
  const [selectedImages, setSelectedImages] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingMode, setProcessingMode] = useState(null);
  const [characterId, setCharacterId] = useState(null);
  const [selectedVariantId, setSelectedVariantId] = useState(null);
  const [loadingSaveCharacter, setLoadingSaveCharacter] = useState(false);


  // ─── Unsaved changes guard ────────────────────────────────────────────────
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const pendingTabRef = useRef(null);

  // ─── Generation history state ─────────────────────────────────────────────
  const [showGenerationHistory, setShowGenerationHistory] = useState(false);
  const [historyImages, setHistoryImages] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  // ─── Refs ─────────────────────────────────────────────────────────────────
  const audioRef = useRef(null);
  const fileInputRef = useRef(null);
  const pollInterval = useRef(null);
  const pollCount = useRef(0);

  // ─── Polling ──────────────────────────────────────────────────────────────
  const pollCharacterStatus = async (id, mode = "create", targetVariantId = null) => {
    pollCount.current += 1;

    // Safety net: max 100 polls (~8 minutes at 5s interval)
    if (pollCount.current > 100) {
      setIsProcessing(false);
      clearInterval(pollInterval.current);
      return;
    }

    try {
      const response = await request.get(`/characters/${id}`);
      const characterData = response.data?.data;
      const status = characterData?.status;
      const variants = characterData?.variants || [];

      if (targetVariantId) {
        // Find the specific variant we are waiting for
        const matchedVariant = variants.find(
          (v) => v._id === targetVariantId || v.id === targetVariantId
        );
        if (matchedVariant) {
          if (matchedVariant.status === "active") {
            // Found and active! Update preview and stop polling
            setCharacterImage(matchedVariant.originalUrl);
            setHistoryImages(variants);
            setSelectedVariantId(matchedVariant._id || matchedVariant.id);
            setIsProcessing(false);
            clearInterval(pollInterval.current);
            pollCount.current = 0;
          }
          // If found but status is not active (e.g. pending/processing), keep polling
        }
        // If targetVariantId is not found in variants yet, keep polling
      } else {
        // Fallback or Create mode logic
        const isStillPending = ["pending", "processing", "editing"].includes(status);
        if (!isStillPending) {
          if (variants.length > 0) {
            const activeVariant = variants[variants.length - 1];
            setCharacterImage(activeVariant.originalUrl);
            setHistoryImages(variants);
            setSelectedVariantId(activeVariant._id || activeVariant.id);
          }
          setIsProcessing(false);
          clearInterval(pollInterval.current);
          pollCount.current = 0;
        }
      }
    } catch (error) {
      console.error("Error polling character status:", error);
      setIsProcessing(false);
      clearInterval(pollInterval.current);
      pollCount.current = 0;
    }
  };

  // ─── Fetch all previously created characters (for history sidebar) ─────────
  const fetchHistory = async () => {
    setIsLoadingHistory(true);
    try {
      const response = await request.get("/characters/public");
      const rawData = response.data?.data;
      // Handle both array and paginated object shapes
      const characters = Array.isArray(rawData)
        ? rawData
        : rawData?.characters || rawData?.data || [];
      setHistoryImages(Array.isArray(characters) ? characters : []);
    } catch (error) {
      console.error("Error fetching character history:", error);
      setHistoryImages([]);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  // ─── Image upload helper ──────────────────────────────────────────────────
  const uploadImage = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    try {
      const response = await request.post("/assets/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response.data?.data?.url || response.data?.url || null;
    } catch (error) {
      console.error("Error uploading image:", error);
      return null;
    }
  };

  // ─── Generate / Re-generate ───────────────────────────────────────────────
  const createPreview = async () => {
    if (!characterName || !selectedGender || !appearance) return;

    const currentPreviewImage = characterImage;

    setIsProcessing(true);
    setProcessingMode(characterId ? "edit" : "create");
    setCharacterImage(null);

    // Upload reference images if any
    let referenceImageUrls = [];
    if (selectedImages.length > 0) {
      const uploadPromises = selectedImages.map((img) => uploadImage(img.file));
      const uploaded = await Promise.all(uploadPromises);
      referenceImageUrls = uploaded.filter(Boolean);
    }

    if (characterId && currentPreviewImage) {
      referenceImageUrls = [currentPreviewImage, ...referenceImageUrls];
    }

    try {
      let newCharacterId = null;
      let pollMode = "create";
      let newVariantId = null;

      if (characterId) {
        // Edit / re-generate flow — update appearance of the loaded character
        const payload = {
          referenceImageUrls,
          editPrompt: appearance,
          ...(selectedVariantId ? { variantId: selectedVariantId } : {}),
        };
        const response = await request.post(
          `/characters/admin/${characterId}/edit`,
          payload
        );
        newCharacterId = response.data?.data?.character?._id || characterId;
        pollMode = "edit";
        newVariantId = response.data?.variantId || response.data?.data?.variantId;
        if (newVariantId) {
          setSelectedVariantId(newVariantId);
        }
      } else {
        // Create flow — brand new character
        const payload = {
          name: characterName,
          prompt: appearance,
          gender: selectedGender,
          language: selectedLanguage.startsWith("en") ? "en" : selectedLanguage,
          isPublic: true,
          referenceImageUrls,
        };
        if (selectedLanguage.startsWith("en")) {
          payload.locale = selectedLanguage === "en-uk" ? "en-GB" : "en-US";
        }
        const response = await request.post(
          "/characters/admin/create-public",
          payload
        );
        newCharacterId = response.data?.data?.character?._id;
        pollMode = "create";
      }

      if (newCharacterId) {
        setCharacterId(newCharacterId);
        pollCount.current = 0;
        if (pollInterval.current) clearInterval(pollInterval.current);

        const targetVariantId = pollMode === "edit" ? newVariantId : null;
        pollCharacterStatus(newCharacterId, pollMode, targetVariantId);
        pollInterval.current = setInterval(() => {
          pollCharacterStatus(newCharacterId, pollMode, targetVariantId);
        }, 5000);
      } else {
        console.error("No character ID returned");
        setIsProcessing(false);
      }
    } catch (error) {
      console.error("Error generating character:", error);
      setIsProcessing(false);
    }
  };

  // ─── Save character ───────────────────────────────────────────────────────
  const saveCharacter = async () => {
    if (!characterId) return;
    setLoadingSaveCharacter(true);

    const payload = {
      name: characterName,
      gender: selectedGender,
      language: selectedLanguage.startsWith("en") ? "en" : selectedLanguage,
      defaultVoice: selectedVoice,
      "baseImage.originalUrl": characterImage,
    };
    if (selectedLanguage.startsWith("en")) {
      payload.locale = selectedLanguage === "en-uk" ? "en-GB" : "en-US";
    }

    try {
      await request.patch(`/characters/${characterId}`, payload);
      // Reset form state
      setCharacterName("");
      setSelectedGender("male");
      setSelectedLanguage("en");
      setAppearance("");
      setSelectedVoice(null);
      setCharacterImage(null);
      setSelectedImages([]);
      setIsProcessing(false);
      setCharacterId(null);
      setSelectedVariantId(null);
      setHistoryImages([]);
      setShowGenerationHistory(false);
      // Navigate back to library
      setActiveTab("library");
    } catch (error) {
      console.error("Error saving character:", error);
    } finally {
      setLoadingSaveCharacter(false);
    }
  };

  // ─── Reference image handling ─────────────────────────────────────────────
  const handleImageUpload = (event) => {
    const files = Array.from(event.target.files).filter((f) =>
      f.type.startsWith("image/")
    );
    const newImages = files.map((file) => ({
      id: Math.random().toString(36).substr(2, 9),
      file,
      preview: URL.createObjectURL(file),
    }));
    setSelectedImages((prev) => [...prev, ...newImages]);
    event.target.value = "";
  };

  const handleRemoveImage = (imageId) => {
    setSelectedImages((prev) => {
      const removed = prev.find((img) => img.id === imageId);
      if (removed) URL.revokeObjectURL(removed.preview);
      return prev.filter((img) => img.id !== imageId);
    });
  };

  // ─── Cleanup effects ──────────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (pollInterval.current) clearInterval(pollInterval.current);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      // Revoke any leftover object URLs
      selectedImages.forEach((img) => URL.revokeObjectURL(img.preview));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Unsaved changes — browser refresh / close guard ─────────────────────
  useEffect(() => {
    const hasUnsavedWork = characterId !== null;
    if (!hasUnsavedWork) return;
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [characterId]);

  // ─── Intercept setActiveTab to show unsaved-changes confirm ──────────────
  const handleSetActiveTab = (tab) => {
    if (characterId !== null) {
      pendingTabRef.current = tab;
      setShowUnsavedModal(true);
    } else {
      setActiveTab(tab);
    }
  };

  const handleConfirmLeave = () => {
    setShowUnsavedModal(false);
    if (pendingTabRef.current) {
      setActiveTab(pendingTabRef.current);
      pendingTabRef.current = null;
    }
  };

  const handleCancelLeave = () => {
    setShowUnsavedModal(false);
    pendingTabRef.current = null;
  };

  // Register the guard function with the parent (Characters.js)
  useEffect(() => {
    if (registerGuard) registerGuard(handleSetActiveTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [characterId]);


  // ─── Helpers ─────────────────────────────────────────────────────────────
  const genderPronoun = (type) => {
    if (type === "possessive") return selectedGender === "female" ? "her" : selectedGender === "male" ? "his" : "their";
    return selectedGender === "female" ? "she" : selectedGender === "male" ? "he" : "they";
  };

  const selectedLanguageLabel =
    LANGUAGES.find((l) => l.value === selectedLanguage)?.label || "Select Language";

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* ── Form Column ─────────────────────────────────────── */}
        <div className="lg:col-span-2">
          <div className="rounded-2xl border border-gray-200 bg-white px-8 pt-6 pb-5">

            {/* Step 1 — Identity */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold bg-orange-500 text-white">1</div>
                <h3 className="form-step-title">Who do you want to create?</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700 selection-label">
                    Name your character
                  </label>
                  <input
                    type="text"
                    placeholder="Type a name (e.g., Sara)"
                    value={characterName}
                    onChange={(e) => setCharacterName(e.target.value)}
                    className="h-12 w-full px-3 rounded-lg border border-gray-300 bg-white text-gray-900 placeholder:text-gray-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700 selection-label">
                    Select a Gender
                  </label>
                  <div className="flex h-12 rounded-lg border border-gray-300 bg-gray-100 overflow-hidden">
                    <button
                      onClick={() => setSelectedGender("female")}
                      className={`px-4 h-full text-sm font-medium transition-all flex items-center justify-center gap-2 flex-1 ${selectedGender === "female"
                        ? "bg-orange-500 text-white"
                        : "text-gray-700 hover:bg-gray-200"
                        }`}
                    >
                      Female
                    </button>
                    <button
                      onClick={() => setSelectedGender("male")}
                      className={`px-4 h-full text-sm font-medium transition-all flex items-center justify-center gap-2 flex-1 border-l border-gray-300 ${selectedGender === "male"
                        ? "bg-orange-500 text-white"
                        : "text-gray-700 hover:bg-gray-200"
                        }`}
                    >
                      Male
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 2 — Language */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold bg-orange-500 text-white">2</div>
                <h3 className="form-step-title">
                  What's {genderPronoun("possessive")} primary language?
                </h3>
              </div>
              <Menu as="div" className="relative">
                <Menu.Button className="gap-2 min-w-[240px] justify-between px-4 py-2 rounded-lg border border-gray-200 bg-orange-50/50 hover:bg-orange-50 hover:border-gray-300 text-gray-900 flex items-center">
                  {selectedLanguageLabel}
                  <i data-lucide="chevron-down" className="h-4 w-4 opacity-50"></i>
                </Menu.Button>
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items className="absolute left-0 mt-2 w-64 origin-top-right rounded-lg border border-gray-200 bg-white shadow-lg z-50 max-h-80 overflow-y-auto">
                    <div className="p-1">
                      {LANGUAGES.map((lang) => (
                        <Menu.Item key={lang.value}>
                          {({ active }) => (
                            <button
                              onClick={() => setSelectedLanguage(lang.value)}
                              className={`w-full text-left px-3 py-2 rounded-md text-sm ${active ? "bg-gray-100 text-gray-900" : "text-gray-900"
                                } ${selectedLanguage === lang.value ? "font-semibold" : ""}`}
                            >
                              {lang.label}
                            </button>
                          )}
                        </Menu.Item>
                      ))}
                    </div>
                  </Menu.Items>
                </Transition>
              </Menu>
            </div>

            {/* Step 3 — Appearance */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold bg-orange-500 text-white">3</div>
                <h3 className="form-step-title">
                  What should {genderPronoun("subject")} look like?
                </h3>
              </div>
              <div className="flex gap-4">
                <div className="flex-1">
                  <textarea
                    placeholder="Describe their age, physical features, hairstyle, outfit, energy, facial expression, lighting style, and the environment they’re in (home, office, outdoors, studio, etc.)."
                    value={appearance}
                    rows={5}
                    onChange={(e) => setAppearance(e.target.value)}
                    className="min-h-32 w-full px-3 py-2 rounded-lg border border-gray-300 bg-white text-gray-900 placeholder:text-gray-400"
                  />
                  {selectedImages.length > 0 && (
                    <div className="mt-3">
                      <p className="text-sm text-gray-600 mb-2">
                        Added images ({selectedImages.length})
                      </p>
                      <div className="flex flex-wrap gap-3">
                        {selectedImages.map((image) => (
                          <div key={image.id} className="relative group">
                            <img
                              src={image.preview}
                              alt="Preview"
                              className="w-20 h-20 rounded-lg object-cover border-2 border-gray-200 group-hover:border-orange-300 transition-colors"
                            />
                            <button
                              onClick={() => handleRemoveImage(image.id)}
                              className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center transition-all opacity-0 group-hover:opacity-100 shadow-lg"
                            >
                              <X className="w-3 h-3 text-white" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Action buttons */}
                <div className="rounded-xl p-4 flex flex-col items-center justify-center gap-3 w-[220px] border border-gray-200 bg-gray-50">
                  <button
                    disabled={!characterName || !selectedGender || !appearance || isProcessing}
                    onClick={createPreview}
                    className="w-full py-3 bg-orange-500 hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-gray-300 disabled:text-gray-500 disabled:hover:bg-gray-300 text-white rounded-xl font-semibold flex items-center justify-center gap-2 transition-all"
                  >
                    {isProcessing ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        {processingMode === "edit" ? "Customizing..." : "Generating..."}
                      </>
                    ) : (
                      characterId ? "Customize Character" : "Generate Preview"
                    )}
                  </button>

                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleImageUpload}
                    accept="image/*"
                    multiple
                    className="hidden"
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full py-3 border border-gray-200 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-xl gap-2 justify-center flex items-center transition-all hover:border-orange-300"
                  >
                    Add image elements
                  </button>


                </div>
              </div>
            </div>

          </div>
        </div>

        {/* ── Preview Column ────────────────────────────────────── */}
        <div className="lg:col-span-1">
          <div className="rounded-2xl border border-gray-200 bg-white p-6 sticky top-6 w-full max-w-[380px] mx-auto lg:ml-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-gray-900">
                Preview character
              </h3>
              {/* Only show if history is applicable */}
              <div
                role="button"
                onClick={() => historyImages.length > 0 && setShowGenerationHistory(true)}
                className="flex items-center gap-1.5 text-sm font-medium text-orange-500 hover:!text-orange-600 px-2 py-1 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"

              >
                <Clock className="w-4 h-4" />
                Previous generations
              </div>
            </div>

            {/* Preview Image / Skeleton Container */}
            <div
              className="rounded-2xl overflow-hidden relative bg-gray-100 w-full max-w-[340px] mx-auto"
              style={{ maxHeight: "600px" }}
            >
              {isProcessing ? (
                <div className="w-full aspect-[9/16] bg-gray-200 animate-pulse flex flex-col items-center justify-center z-10">
                  <div className="relative">
                    <Loader2 className="w-10 h-10 text-orange-500 animate-spin" />
                    <Sparkles className="w-5 h-5 text-orange-400 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
                  </div>
                  <p className="mt-4 text-gray-500 font-medium text-sm animate-pulse">
                    Generating New Scene...
                  </p>
                </div>
              ) : (
                <>
                  {characterImage ? (
                    <img
                      src={characterImage}
                      alt="Character preview"
                      className="w-full aspect-[9/16] "
                    />
                  ) : (
                    <div className="flex items-center justify-center w-full aspect-[9/16] text-gray-500 text-sm">
                      Select a character to preview
                    </div>
                  )}

                  {/* Optional Top-Left Badge */}
                  {/* {characterImage && (
                    <div className="absolute top-3 left-3 flex gap-2">
                      <span className="bg-orange-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full uppercase">
                        Character
                      </span>
                    </div>
                  )} */}
                </>
              )}
            </div>

            <button
              disabled={
                !characterName ||
                !selectedGender ||
                !appearance ||
                !characterId ||
                !characterImage ||
                isProcessing ||
                loadingSaveCharacter
              }
              className="w-full mt-6 h-12 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl font-semibold shadow-lg shadow-orange-500/30 flex items-center justify-center gap-2"
              onClick={saveCharacter}
            >
              {loadingSaveCharacter ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Character to Library"
              )}
            </button>
            <p className="text-xs text-center mt-3 text-gray-400">
              Characters generated & saved here are visible to all users in the library.
            </p>
          </div>
        </div>
      </div>

      {/* Generation History Sidebar */}
      <GenerationHistorySidebar
        show={showGenerationHistory}
        onClose={() => setShowGenerationHistory(false)}
        images={historyImages}
        loading={isLoadingHistory}
        onSelectVariant={(variant) => {
          // variant is an imageVariants item: { _id, originalUrl, source, createdAt }
          setCharacterImage(variant.originalUrl);
          setSelectedVariantId(variant._id);
          setShowGenerationHistory(false);
        }}
      />

      {/* Unsaved Changes Confirm Modal */}
      <ConfirmModal
        isOpen={showUnsavedModal}
        onClose={handleCancelLeave}
        title="Unsaved Changes"
        message="You have a character that hasn't been saved yet. If you leave now, your progress will be lost. Are you sure you want to leave?"
        confirmLabel="Leave Anyway"
        cancelLabel="Stay"
        onConfirm={handleConfirmLeave}
        onCancel={handleCancelLeave}
      />
    </>
  );
}
